import { BaseModel, type RelationConfig } from './base';
import { dateYMDRelation, dateRelation } from '../lib/dates';
import { DateYMD } from '../lib/dates';
import { CustomField } from './customField';
import { CustomerPass } from './customerPass';
import { Employee } from './employee';
import { EmployeeHour } from './employeeHour';
import { EmployeeBreak } from './employeeBreak';
import { OverviewBlock } from './overviewBlock';
import { Recurrence } from './recurrence';
import { Reservation } from './reservation';
import { ReservationCustomer } from './reservationCustomer';
import { Service } from './service';
import { ServiceResource } from './serviceResource';
import { Voucher } from './voucher';
import { Waitlist } from './waitlist';

// Enums

export enum AvailabilityRequestDateMode {
  all = 0,
  onePerDay = 1,
  onePerRequest = 2,
  none = 3,
}

export const AvailabilityRequestDateModeLabels: Record<AvailabilityRequestDateMode, string> = {
  [AvailabilityRequestDateMode.all]: 'api.models.booking.all',
  [AvailabilityRequestDateMode.onePerDay]: 'api.models.booking.onePerDay',
  [AvailabilityRequestDateMode.onePerRequest]: 'api.models.booking.onePerRequest',
  [AvailabilityRequestDateMode.none]: 'general.none',
};

export enum AvailabilityRequestGroupMode {
  reservation = 0,
  customer = 1,
}

export const AvailabilityRequestGroupModeLabels: Record<AvailabilityRequestGroupMode, string> = {
  [AvailabilityRequestGroupMode.reservation]: 'general.reservation',
  [AvailabilityRequestGroupMode.customer]: 'general.customer',
};

export enum ConfirmationRequestGroupMode {
  reservation = 0,
  customer = 1,
}

export const ConfirmationRequestGroupModeLabels: Record<ConfirmationRequestGroupMode, string> = {
  [ConfirmationRequestGroupMode.reservation]: 'general.reservation',
  [ConfirmationRequestGroupMode.customer]: 'general.customer',
};

export enum BookingRequestType {
  serviceType = 0,
  classType = 1,
  timetableType = 2,
}

export const BookingRequestTypeLabels: Record<BookingRequestType, string> = {
  [BookingRequestType.serviceType]: 'general.service',
  [BookingRequestType.classType]: 'general.class',
  [BookingRequestType.timetableType]: 'general.timetable',
};

// Booking Helper Classes

export class BookingSection extends BaseModel {
  date!: DateYMD;
  employee!: Employee;
  hour!: EmployeeHour;
  breaks!: EmployeeBreak[];
  blocks!: OverviewBlock[];

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
      employee: { type: Employee },
      hour: { type: EmployeeHour },
      breaks: { type: EmployeeBreak },
      blocks: { type: OverviewBlock },
    };
  }
}

export class BookingDatesInfoRoster extends BaseModel {
  idEmployee!: number;
  employee!: Employee | null;
  hour!: EmployeeHour;
  breaks!: EmployeeBreak[];

  static override relations(): RelationConfig {
    return {
      employee: { type: Employee },
      hour: { type: EmployeeHour },
      breaks: { type: EmployeeBreak },
    };
  }
}

export class BookingDatesInfo extends BaseModel {
  date!: DateYMD;
  reservations!: Reservation[];
  rosters!: BookingDatesInfoRoster[];

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
      reservations: { type: Reservation },
      rosters: { type: BookingDatesInfoRoster },
    };
  }
}

export class BookingDates extends BaseModel {
  dates!: BookingDatesInfo[];
  services!: Service[];
  employees!: Employee[];
  breaks!: EmployeeBreak[];

  static override relations(): RelationConfig {
    return {
      dates: { type: BookingDatesInfo },
      services: { type: Service },
      employees: { type: Employee },
      breaks: { type: EmployeeBreak },
    };
  }

  constructor(data?: Record<string, any>) {
    super(data);

    // Link employees and services to reservations after hydration
    if (this.dates) {
      for (const dateInfo of this.dates) {
        if (dateInfo.rosters) {
          for (const roster of dateInfo.rosters) {
            roster.employee = this.employeeForID(roster.idEmployee) ?? null;
          }
        }

        if (dateInfo.reservations) {
          for (const reservation of dateInfo.reservations) {
            if ((reservation as any).services) {
              for (const reservationService of (reservation as any).services) {
                reservationService.service = this.serviceForID(reservationService.idService);
                reservationService.employee = this.employeeForID(reservationService.idEmployee);

                if (reservationService.resources) {
                  for (const resource of reservationService.resources) {
                    resource.resource = this.employeeForID(resource.idEmployee);
                  }
                }
              }
            }

            if ((reservation as any).attendees?.resources) {
              for (const resource of (reservation as any).attendees.resources) {
                resource.resource = this.employeeForID(resource.idEmployee);
              }
            }
          }
        }
      }
    }
  }

  employeeForID(id: number): Employee | undefined {
    return this.employees?.find(employee => employee.idEmployee === Number(id));
  }

  serviceForID(id: number): Service | undefined {
    return this.services?.find(service => (service as any).idService === Number(id));
  }
}

export class BookingStatsDateInfoEmployeeStat extends BaseModel {}

export class BookingStatsDateInfo extends BaseModel {
  date!: DateYMD;
  employees!: BookingStatsDateInfoEmployeeStat[];

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
      employees: { type: BookingStatsDateInfoEmployeeStat },
    };
  }
}

export class BookingStats extends BaseModel {
  dates!: BookingStatsDateInfo[];

  static override relations(): RelationConfig {
    return {
      dates: { type: BookingStatsDateInfo },
    };
  }
}

export class BookingValidationService extends BaseModel {
  services!: Service[];
  passes!: CustomerPass[];
  vouchers!: Voucher[];

  static override relations(): RelationConfig {
    return {
      passes: { type: CustomerPass },
      services: { type: Service },
      vouchers: { type: Voucher },
    };
  }
}

export class BookingValidation extends BaseModel {
  date!: DateYMD;
  services!: BookingValidationService[];

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
      services: { type: BookingValidationService },
    };
  }
}

export class BookingDefaults extends BaseModel {
  reservationCustomFields!: CustomField[];
  reservationCustomerCustomFields!: CustomField[];

  static override relations(): RelationConfig {
    return {
      reservationCustomFields: { type: CustomField },
      reservationCustomerCustomFields: { type: CustomField },
    };
  }
}

// Availability System

export class AvailabilityRequestServiceResource extends BaseModel {
  serviceResource!: ServiceResource;
  resources!: Employee[];

  static override relations(): RelationConfig {
    return {
      resources: { type: Employee },
    };
  }

  override toJSON(): Record<string, any> {
    return {
      serviceResource: this.serviceResource?.idServiceResource,
      resources: this.resources?.map(resource => (resource as any).idEmployee || resource),
    };
  }
}

export class AvailabilityRequestService extends BaseModel {
  employee!: Employee | null;
  service!: Service | null;
  idAssociation!: number | null;
  resources!: AvailabilityRequestServiceResource[] | null;
  groupMode!: AvailabilityRequestGroupMode;
  price!: number;
  duration!: number;
  processing!: number;
  finishing!: number;
  bufferTimePre!: number;
  bufferTimePost!: number;
  bufferTimeMode!: number | null;

  static override relations(): RelationConfig {
    return {
      employee: { type: Employee },
      service: { type: Service },
      resources: { type: AvailabilityRequestServiceResource },
    };
  }

  override toJSON(): Record<string, any> {
    const compare = (key: string): any => {
      const service = this.service as any;
      const value = (this as any)[key];
      const base = service?.[key];
      return base !== value ? value : null;
    };

    const object: Record<string, any> = {
      price: this.price,
      duration: this.duration,
      processing: this.processing,
      finishing: this.finishing,
      bufferTimePre: compare('bufferTimePre'),
      bufferTimePost: compare('bufferTimePost'),
      bufferTimeMode: compare('bufferTimeMode'),
      groupMode: this.groupMode,
    };

    if (this.service) object.idService = (this.service as any).idService;
    if (this.idAssociation != null) {
      object.idAssociation = this.idAssociation;
    } else if ((this.service as any)?.idAssociation != null) {
      object.idAssociation = (this.service as any).idAssociation;
    }
    if (this.employee) object.idEmployee = this.employee.idEmployee;
    if (this.resources) {
      object.resources = this.resources.map(resource => resource.toJSON());
    }

    return object;
  }

  static forReservationService(
    reservationService: any,
    { includeEmployees = true, groupMode = AvailabilityRequestGroupMode.reservation } = {}
  ): AvailabilityRequestService {
    const requestService = new AvailabilityRequestService();
    if (includeEmployees) {
      requestService.employee = reservationService.employee;
    }
    requestService.service = reservationService.service;
    requestService.idAssociation = reservationService.idAssociation
      ?? reservationService.association?.idAssociation
      ?? reservationService.service?.idAssociation
      ?? null;
    requestService.duration = reservationService.duration;
    requestService.processing = reservationService.processing;
    requestService.finishing = reservationService.finishing;
    requestService.bufferTimePre = reservationService.bufferTimePre;
    requestService.bufferTimePost = reservationService.bufferTimePost;
    requestService.bufferTimeMode = reservationService.bufferTimeMode?.value ?? reservationService.bufferTimeMode ?? null;
    requestService.groupMode = groupMode;
    requestService.price = reservationService.price;

    if (reservationService.resources?.length > 0) {
      const resources: Record<string, AvailabilityRequestServiceResource> = {};

      for (const reservationServiceResource of reservationService.resources) {
        const idServiceResource = reservationServiceResource.serviceResource.idServiceResource;
        if (resources[idServiceResource]) {
          resources[idServiceResource].resources.push(reservationServiceResource.resource);
        } else {
          const newResource = new AvailabilityRequestServiceResource();
          newResource.serviceResource = reservationServiceResource.serviceResource;
          newResource.resources = [reservationServiceResource.resource];
          resources[idServiceResource] = newResource;
        }
      }

      requestService.resources = Object.values(resources);
    }

    return requestService;
  }
}

export class AvailabilityRequest extends BaseModel {
  dates!: Date[];
  dateMode!: AvailabilityRequestDateMode;
  services!: AvailabilityRequestService[];
  excludeReservations!: any[];
  excludeReservationCustomers!: any[];
  includeSlots!: boolean;

  constructor(data?: Record<string, any>) {
    super(data);
    if (!data) {
      this.dateMode = AvailabilityRequestDateMode.all;
      this.includeSlots = true;
      this.dates = [];
      this.services = [];
      this.excludeReservations = [];
      this.excludeReservationCustomers = [];
    }
  }

  override toJSON(): Record<string, any> {
    return {
      dates: this.dates?.map(date => Math.floor(date.getTime() / 1000)),
      dateMode: this.dateMode ?? AvailabilityRequestDateMode.all,
      services: this.services?.map(service => service.toJSON()),
      excludeReservations: this.excludeReservations?.map(r => r.idReservation || r.id || 0),
      excludeReservationCustomers: this.excludeReservationCustomers?.map(rc => rc.idReservationCustomer),
      includeSlots: this.includeSlots,
    };
  }

  static forReservation(
    reservation: any,
    dates: Date[],
    {
      includeEmployees = true,
      groupMode = AvailabilityRequestGroupMode.reservation,
      reservationCustomer,
    }: {
      includeEmployees?: boolean;
      groupMode?: AvailabilityRequestGroupMode;
      reservationCustomer?: any;
    } = {}
  ): AvailabilityRequest {
    const request = new AvailabilityRequest();
    const services = reservation.services ?? [reservation];
    request.dates = dates;
    request.excludeReservations = reservation ? [reservation] : [];
    request.excludeReservationCustomers = reservationCustomer ? [reservationCustomer] : [];

    request.services = services.map((reservationService: any) =>
      AvailabilityRequestService.forReservationService(reservationService, { includeEmployees, groupMode })
    );

    return request;
  }
}

export class AvailabilitySlotServiceResource extends BaseModel {
  serviceResource!: any;
  resources!: Employee[];

  static override relations(): RelationConfig {
    return {
      resources: { type: Employee },
    };
  }
}

export class AvailabilitySlotService extends BaseModel {
  employee!: Employee;
  service!: Service;
  idAssociation!: number | null;
  reservation!: Reservation | null;
  customerPass!: CustomerPass | null;
  voucher!: Voucher | null;
  startTime!: number;
  slot!: number;
  duration!: number;
  processing!: number;
  finishing!: number;
  bufferTimePre!: number;
  bufferTimePost!: number;
  bufferTimeMode!: number | null;
  price!: number;
  resources!: AvailabilitySlotServiceResource[];

  static override relations(): RelationConfig {
    return {
      customerPass: { type: CustomerPass },
      employee: { type: Employee },
      service: { type: Service },
      reservation: { type: Reservation },
      resources: { type: AvailabilitySlotServiceResource },
      voucher: { type: Voucher },
    };
  }
}

export class AvailabilitySlot extends BaseModel {
  date!: Date | null;
  services!: AvailabilitySlotService[];

  static override relations(): RelationConfig {
    // `date` comes off the wire as a Unix timestamp (seconds) — without
    // `dateRelation` it would stay as a number and blow up any consumer
    // that expects a Date (DateYMD.fromDate, $date.utc, etc.). Matches the
    // Nuxt 2 `{ type: Date }` relation on this model.
    return {
      date: dateRelation,
      services: { type: AvailabilitySlotService },
    };
  }

  get employeeLabel(): string {
    return this.services?.map(service => (service.employee as any)?.displayName).join(', ') ?? '';
  }

  get hasResources(): boolean {
    return this.services?.some(service => service.resources?.length > 0) ?? false;
  }
}

export class AvailabilityDate extends BaseModel {
  date!: Date | null;
  slots!: AvailabilitySlot[];

  static override relations(): RelationConfig {
    // Same as AvailabilitySlot above — dateRelation converts the server's
    // Unix timestamp into a Date instance.
    return {
      date: dateRelation,
      slots: { type: AvailabilitySlot },
    };
  }
}

export class AvailabilityResponse extends BaseModel {
  dates!: AvailabilityDate[];

  static override relations(): RelationConfig {
    return {
      dates: { type: AvailabilityDate },
    };
  }
}

// Confirmation Request System

export class ConfirmationRequestServiceResource extends BaseModel {
  serviceResource!: ServiceResource | null;
  resource!: Employee | null;

  override toJSON(): Record<string, any> {
    return {
      idServiceResource: this.serviceResource?.idServiceResource,
      idEmployee: this.resource?.idEmployee || this.resource || null,
    };
  }
}

export class ConfirmationRequestService extends BaseModel {
  employee!: Employee | null;
  service!: Service | null;
  idAssociation!: number | null;
  reservation!: Reservation | null;
  resources!: ConfirmationRequestServiceResource[] | null;
  customerPass!: CustomerPass | null;
  voucher!: Voucher | null;
  groupMode!: ConfirmationRequestGroupMode | null;
  slot!: number | null;
  startTime!: number | null;
  duration!: number;
  processing!: number;
  finishing!: number;
  price!: number;
  bufferTimePre!: number;
  bufferTimePost!: number;
  bufferTimeMode!: number | null;

  static override relations(): RelationConfig {
    return {
      customerPass: { type: CustomerPass },
      employee: { type: Employee },
      service: { type: Service },
      reservation: { type: Reservation },
      resources: { type: ConfirmationRequestServiceResource },
      voucher: { type: Voucher },
    };
  }

  override toJSON(): Record<string, any> {
    const object: Record<string, any> = {
      slot: this.slot,
      startTime: this.startTime != null ? Number(this.startTime) : null,
      duration: this.duration,
      processing: this.processing,
      finishing: this.finishing,
      price: this.price,
      bufferTimePre: this.bufferTimePre,
      bufferTimePost: this.bufferTimePost,
      bufferTimeMode: this.bufferTimeMode,
    };

    if (this.service) object.idService = (this.service as any).idService;
    if (this.idAssociation != null) {
      object.idAssociation = this.idAssociation;
    } else if ((this.service as any)?.idAssociation != null) {
      object.idAssociation = (this.service as any).idAssociation;
    }
    if (this.employee) object.idEmployee = this.employee.idEmployee;
    if (this.reservation) object.idReservation = (this.reservation as any).idReservation;
    if (this.resources) {
      object.resources = this.resources
        .filter(resource => resource?.resource)
        .map(resource => resource.toJSON());
    }
    if (this.customerPass?.idCustomerPass) {
      object.idCustomerPass = this.customerPass.idCustomerPass;
    } else if (this.customerPass === null) {
      object.idCustomerPass = null;
    }
    if (this.voucher?.idVoucher) {
      object.idVoucher = this.voucher.idVoucher;
    } else if (this.voucher === null) {
      object.idVoucher = null;
    }
    if (this.groupMode != null) {
      object.groupMode = this.groupMode;
    }

    return object;
  }

  static fromSlotService(availabilitySlotService: AvailabilitySlotService, date: Date | null = null): ConfirmationRequestService {
    const requestService = new ConfirmationRequestService();
    requestService.employee = availabilitySlotService.employee;
    requestService.service = availabilitySlotService.service;
    requestService.idAssociation = availabilitySlotService.idAssociation
      ?? (availabilitySlotService.service as any)?.idAssociation
      ?? null;
    requestService.reservation = availabilitySlotService.reservation;
    requestService.startTime = availabilitySlotService.startTime;
    requestService.slot = availabilitySlotService.slot;
    if ((!Number.isFinite(Number(requestService.slot)) || Number(requestService.slot) <= 0) && date && availabilitySlotService.startTime != null) {
      const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const total = new Date(utc.getTime() + Number(availabilitySlotService.startTime) * 60000);
      requestService.slot = Math.floor(total.getTime() / 1000);
    }
    requestService.duration = availabilitySlotService.duration;
    requestService.processing = availabilitySlotService.processing;
    requestService.finishing = availabilitySlotService.finishing;
    requestService.bufferTimePre = availabilitySlotService.bufferTimePre;
    requestService.bufferTimePost = availabilitySlotService.bufferTimePost;
    requestService.bufferTimeMode = availabilitySlotService.bufferTimeMode;
    requestService.price = availabilitySlotService.price;
    requestService.customerPass = availabilitySlotService.customerPass;
    requestService.voucher = availabilitySlotService.voucher;

    if (availabilitySlotService.resources?.length > 0) {
      requestService.resources = [];

      for (const slotResource of availabilitySlotService.resources) {
        if ((slotResource as any).resources) {
          for (const employee of (slotResource as any).resources) {
            const requestResource = new ConfirmationRequestServiceResource();
            requestResource.serviceResource = slotResource.serviceResource;
            requestResource.resource = employee;
            requestService.resources.push(requestResource);
          }
        }
        if ((slotResource as any).resource) {
          const requestResource = new ConfirmationRequestServiceResource();
          requestResource.serviceResource = slotResource.serviceResource;
          requestResource.resource = (slotResource as any).resource;
          requestService.resources.push(requestResource);
        }
      }
    }

    return requestService;
  }

  static fromReservationService(reservation: any, reservationService: any): ConfirmationRequestService {
    const requestService = new ConfirmationRequestService();
    requestService.employee = reservationService.employee;
    requestService.service = reservationService.service;
    requestService.idAssociation = reservationService.idAssociation
      ?? reservationService.association?.idAssociation
      ?? reservationService.service?.idAssociation
      ?? null;
    requestService.startTime = reservationService.startTime;
    requestService.duration = reservationService.duration;
    requestService.processing = reservationService.processing;
    requestService.finishing = reservationService.finishing;
    requestService.bufferTimePre = reservationService.bufferTimePre;
    requestService.bufferTimePost = reservationService.bufferTimePost;
    requestService.bufferTimeMode = reservationService.bufferTimeMode?.value ?? reservationService.bufferTimeMode ?? null;
    requestService.price = reservationService.price;

    // Calculate slot from date + startTime
    const date = reservation.date?.date ?? reservation.date;
    if (date) {
      const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const total = new Date(utc.getTime() + reservationService.startTime * 60000);
      requestService.slot = Math.floor(total.getTime() / 1000);
    }

    if (reservationService.resources?.length > 0) {
      requestService.resources = [];

      for (const reservationServiceResource of reservationService.resources) {
        if (reservationServiceResource.resources) {
          for (const employee of reservationServiceResource.resources) {
            const requestResource = new ConfirmationRequestServiceResource();
            requestResource.serviceResource = reservationServiceResource.serviceResource;
            requestResource.resource = employee;
            requestService.resources.push(requestResource);
          }
        }
        if (reservationServiceResource.resource) {
          const requestResource = new ConfirmationRequestServiceResource();
          requestResource.serviceResource = reservationServiceResource.serviceResource;
          requestResource.resource = reservationServiceResource.resource;
          requestService.resources.push(requestResource);
        }
      }
    }

    return requestService;
  }

  static fromGroupReservationService(
    reservation: any,
    reservationService: any,
    resources: any[] | null,
    customPrice: number | null = null
  ): ConfirmationRequestService {
    const requestService = new ConfirmationRequestService();
    requestService.employee = reservationService.employee;
    requestService.service = reservationService.service;
    requestService.idAssociation = reservationService.idAssociation
      ?? reservationService.association?.idAssociation
      ?? reservationService.service?.idAssociation
      ?? null;
    requestService.reservation = reservation;
    requestService.startTime = reservationService.startTime;
    requestService.duration = reservationService.duration;
    requestService.processing = reservationService.processing;
    requestService.finishing = reservationService.finishing;
    requestService.bufferTimePre = reservationService.bufferTimePre;
    requestService.bufferTimePost = reservationService.bufferTimePost;
    requestService.bufferTimeMode = reservationService.bufferTimeMode?.value ?? reservationService.bufferTimeMode ?? null;
    requestService.price = customPrice !== null ? customPrice : reservationService.price;
    requestService.groupMode = ConfirmationRequestGroupMode.customer;

    // Calculate slot from date + startTime
    const date = reservation.date?.date ?? reservation.date;
    if (date) {
      const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const total = new Date(utc.getTime() + reservationService.startTime * 60000);
      requestService.slot = Math.floor(total.getTime() / 1000);
    }

    if (resources?.length) {
      requestService.resources = resources.map(reservationServiceResource => {
        const requestResource = new ConfirmationRequestServiceResource();
        requestResource.serviceResource = reservationServiceResource.serviceResource;
        requestResource.resource = reservationServiceResource.resource || 0;
        return requestResource;
      });
    }

    return requestService;
  }
}

export class ConfirmationRequest extends BaseModel {
  date!: DateYMD;
  services!: ConfirmationRequestService[];
  customFields!: CustomField[] | null;
  customer!: any;
  reservation!: Reservation | null;
  reservationCustomer!: ReservationCustomer | null;
  recurrence!: Recurrence | null;
  customerRecurrence!: any;
  notes!: string | null;
  customerType!: number;
  customerRequestType!: number;
  overrideTime!: boolean;
  sendEmail!: boolean;
  allowInternet!: boolean;
  customerMax!: number;
  waitlist!: Waitlist | null;
  rebookReservation!: Reservation | null;
  rebookReservationCustomer!: ReservationCustomer | null;

  constructor(data?: Record<string, any>) {
    super(data);
    if (!data) {
      this.overrideTime = false;
      this.sendEmail = true;
    }
  }

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
      services: { type: ConfirmationRequestService },
      reservation: { type: Reservation },
    };
  }

  override toJSON(): Record<string, any> {
    const object: Record<string, any> = {
      notes: this.notes,
      date: (this.date as any)?.integer ?? this.date,
      customerType: this.customerType,
      customerRequestType: this.customerRequestType,
      services: this.services?.map(service => service.toJSON()),
      idCustomer: this.customer ? Number(this.customer.idCustomer) : null,
      idReservation: this.reservation ? (this.reservation as any).idReservation : null,
      idReservationCustomer: this.reservationCustomer ? this.reservationCustomer.idReservationCustomer : null,
      recurrence: this.recurrence?.toJSON?.() ?? this.recurrence ?? null,
      customerRecurrence: this.customerRecurrence,
      allowInternet: this.allowInternet,
      customerMax: this.customerMax,
      idWaitlist: this.waitlist ? Number(this.waitlist.idWaitlist) : null,
      rebookIdReservation: this.rebookReservation ? Number(this.rebookReservation.idReservation) : null,
      rebookIdReservationCustomer: this.rebookReservationCustomer ? this.rebookReservationCustomer.idReservationCustomer : null,
    };

    if (this.customFields) {
      object.customFields = this.customFields.map((customField: any) => ({
        idCustomField: customField.idCustomField,
        value: customField.value,
      }));
    }

    return object;
  }

  static fromReservation(
    reservation: any,
    {
      customFields = null,
      customer = null,
      reservationCustomer = null,
      customerType = 0,
      customerRequestType = 0,
    }: {
      customFields?: any[] | null;
      customer?: any;
      reservationCustomer?: any;
      customerType?: number;
      customerRequestType?: number;
    } = {}
  ): ConfirmationRequest {
    const request = new ConfirmationRequest();
    request.reservation = reservation;
    request.date = reservation.date;
    request.allowInternet = reservation.allowInternet;
    request.services = reservation.services.map((rs: any) =>
      ConfirmationRequestService.fromReservationService(reservation, rs)
    );
    request.customFields = customFields;
    request.customer = customer;
    request.reservationCustomer = reservationCustomer;
    request.customerType = customerType;
    request.customerRequestType = customerRequestType;

    return request;
  }

  static fromGroupReservation(
    reservation: any,
    {
      customFields = null,
      customer = null,
      reservationCustomer = null,
      customerType = 0,
      customerRequestType = 0,
      resources = null,
      customerRecurrence = null,
      notes = null,
      price = null,
    }: {
      customFields?: any[] | null;
      customer?: any;
      reservationCustomer?: any;
      customerType?: number;
      customerRequestType?: number;
      resources?: any[] | null;
      customerRecurrence?: any;
      notes?: string | null;
      price?: number | null;
    } = {}
  ): ConfirmationRequest {
    const request = new ConfirmationRequest();
    request.reservation = reservation;
    request.date = reservation.date;
    request.allowInternet = reservation.allowInternet;
    request.services = reservation.services.map((rs: any) =>
      ConfirmationRequestService.fromGroupReservationService(reservation, rs, resources, price)
    );
    request.customFields = customFields;
    request.customer = customer;
    request.reservationCustomer = reservationCustomer;
    request.customerType = customerType;
    request.customerRequestType = customerRequestType;
    request.notes = notes;

    if (customerRecurrence) {
      request.customerRecurrence = customerRecurrence;
    }

    return request;
  }

  static fromSlot(
    data: any,
    {
      customFields = null,
      customer = null,
      reservationCustomer = null,
      notes = null,
      move = null,
      customerType = 0,
      customerRequestType = 0,
      overrideTime = false,
      allowInternet = true,
      customerMax = 0,
      sendEmail = true,
      recurrence = null,
      customerRecurrence = null,
      waitlist = null,
      rebookReservation = null,
      rebookReservationCustomer = null,
    }: {
      customFields?: any[] | null;
      customer?: any;
      reservationCustomer?: any;
      notes?: string | null;
      move?: any;
      customerType?: number;
      customerRequestType?: number;
      overrideTime?: boolean;
      allowInternet?: boolean;
      customerMax?: number;
      sendEmail?: boolean;
      recurrence?: any;
      customerRecurrence?: any;
      waitlist?: any;
      rebookReservation?: any;
      rebookReservationCustomer?: any;
    } = {}
  ): ConfirmationRequest {
    const request = new ConfirmationRequest();
    request.date = DateYMD.fromDate(data.date);
    request.services = data.services.map((rs: any) =>
      ConfirmationRequestService.fromSlotService(rs, data.date),
    );
    request.customFields = customFields;
    request.customer = customer;
    request.reservation = move;
    request.reservationCustomer = reservationCustomer;
    request.notes = notes;
    request.customerType = customerType;
    request.customerRequestType = customerRequestType;
    request.overrideTime = overrideTime;
    request.allowInternet = allowInternet;
    request.customerMax = customerMax;
    request.sendEmail = sendEmail;
    request.waitlist = waitlist;
    request.rebookReservation = rebookReservation;
    request.rebookReservationCustomer = rebookReservationCustomer;

    // Override allowInternet from first service
    const firstService = request.services[0];
    if (firstService?.service) {
      request.allowInternet = firstService.service.allowInternet;
    }

    if (recurrence) {
      request.recurrence = recurrence;
    }

    if (customerRecurrence) {
      request.customerRecurrence = customerRecurrence;
    }

    return request;
  }
}

// Main Booking Model

export class Booking extends BaseModel {
  idBooking!: number;
  date!: DateYMD;
  sections!: BookingSection[];

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
      sections: { type: BookingSection },
    };
  }

  override objectID(): string | null {
    return this.idBooking?.toString() ?? null;
  }
}
